(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
    typeof define === 'function' && define.amd ? define(['exports'], factory) :
    (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global['ot-lib-eventbus'] = {}));
}(this, (function (exports) { 'use strict';

    /**
     *  Used as a singleton event channel, which does not need any initialization.
     *  Events can be listened to and emitted from any stage and anywhere.
     *  Events are emitted over a tree of listeners, e.g. for `action/add-ticket/123`,
     *  all listeners on `action`, `action/add-ticket` and `action/add-ticket/123` are
     *  triggered.
     */
    class EventBus {
        constructor(config) {
            // Registered listeners
            this.listeners = {};
            // Internal map that keeps listener metadata by id
            this._map = {};
            this._config = config || {};
        }
        // Subscribe to events by path
        on(path, listener) {
            if (path.length < 1) {
                this.listeners.listeners = this.listeners.listeners || [];
                this.listeners.listeners.push(listener);
            }
            else {
                // Set the root of the tree
                let root = this.listeners;
                // Itererate over the path
                for (let i = 0; i < path.length; i++) {
                    // Get the next node
                    const node = root[path[i]];
                    // If it is undefined, define it
                    if (!node) {
                        root[path[i]] = {};
                    }
                    // Set the new root
                    root = root[path[i]];
                    // Check if it is the last part of the path
                    if (i === (path.length - 1)) {
                        // Make sure listeners is not undefined
                        root.listeners = root.listeners || [];
                        // Add the listener
                        root.listeners.push(listener);
                    }
                }
            }
            const id = this._generateId();
            this._map[id] = {
                path,
                listener,
            };
            return id;
        }
        /**
         * Remove a listener from the event bus
         * @param id - as provided by the 'on' method
         */
        off(id) {
            var _a, _b;
            if (!this._map[id]) {
                throw Error('Listener does not exist');
            }
            const path = this._map[id].path;
            const listener = this._map[id].listener;
            let root = this.listeners;
            while (path.length > 0) {
                const part = path.shift();
                root = root[part];
                if (path.length < 1) {
                    const index = (_a = root.listeners) === null || _a === void 0 ? void 0 : _a.findIndex((value) => value === listener);
                    if (index === undefined || index < 0) {
                        throw Error('Listener not found in tree');
                    }
                    (_b = root.listeners) === null || _b === void 0 ? void 0 : _b.splice(index, 1);
                }
            }
            delete this._map[id];
        }
        // Emit data to it's listeners by path
        emit(path, data) {
            // If the env is development, log all events to the console
            if (this._config.isDebug) {
                // eslint-disable-next-line no-console
                console.info('EVENT', path, data);
            }
            // Array of relevant listeners
            let to = [];
            // Set the root of the tree
            let root = this.listeners;
            // Iterate over the path's parts
            for (let i = 0; i <= path.length; i++) {
                // If the current node has listeners,
                // add them to the list of relevant listeners
                if (root.listeners) {
                    to = [...to, ...root.listeners];
                }
                // Check if the next node exists
                if (root[path[i]]) {
                    // If it does, set it as new root
                    root = root[path[i]];
                }
                else {
                    // Otherwise, stop iterating
                    break;
                }
            }
            // Notify all listeners
            for (const listener of to) {
                const pathCopy = [...path];
                if (data && typeof data === 'object') {
                    data = JSON.parse(JSON.stringify(data));
                }
                setTimeout(() => {
                    listener(pathCopy, data);
                }, 0);
            }
        }
        /**
         * Basic id generator
         * src: https://www.w3resource.com/javascript-exercises/javascript-math-exercise-23.php
         */
        _generateId() {
            let dt = new Date().getTime();
            const id = 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, function (c) {
                var r = (dt + Math.random() * 16) % 16 | 0;
                dt = Math.floor(dt / 16);
                return (c == 'x' ? r : (r & 0x3 | 0x8)).toString(16);
            });
            return id;
        }
    }

    class EventScope {
        constructor(eventBus) {
            this._eventBus = eventBus;
        }
        /**
         *  Listen to events by providing a path and a callback to the `on` function
         *  @param path - Path to listen to
         *  @param cb - Will be called when an event occurs
         */
        on(path, cb, noScope = false) {
            // If the path is not yet an array, use it as a single path segment
            if (!Array.isArray(path)) {
                if (path === '*') {
                    path = [];
                }
                else {
                    path = [path];
                }
            }
            else {
                path = [...path];
            }
            // If the eventScope is set, prefix the path with it
            if (this._eventScope && !noScope) {
                path = [this._eventScope, ...path];
            }
            // Relay to the actual event bus
            return this._eventBus.on(path, cb);
        }
        /**
         * Remove a listener from the event bus
         * @param id - listener id as provided by the 'on' method
         */
        off(id) {
            this._eventBus.off(id);
        }
        /**
         *  Emit an event from the eventScope
         *  @param path - The path on which the event occurred
         *  @param payload - The data to be send
         */
        emit(path, payload, noScope = false) {
            // If the path is not yet an array, use it as a single path segment
            if (!Array.isArray(path)) {
                path = [path];
            }
            // If the eventScope is set, prefix the path with it
            if (this._eventScope && !noScope) {
                path = [this._eventScope, ...path];
            }
            // Relay to the actual event bus
            this._eventBus.emit(path, payload);
        }
    }

    const EventBusSingleton = new EventBus();

    exports.EventBusSingleton = EventBusSingleton;
    exports.EventScope = EventScope;
    exports.default = EventBus;

    Object.defineProperty(exports, '__esModule', { value: true });

})));
